import * as Sentry from '@sentry/vue'
import App from '@/App.vue'

// 初始化 Sentry
export function initSentry(VueApp) {
  if (!process.env.VUE_APP_MONITOR_SENTRY_DNS) {
    console.warn('[Sentry] 未配置 DSN，跳过初始化')
    return
  }

  Sentry.init({
    app: VueApp || App,
    dsn: process.env.VUE_APP_MONITOR_SENTRY_DNS,
    sendDefaultPii: true,
    environment: process.env.NODE_ENV,
    release: process.env.VUE_APP_VERSION || 'unknown',
    // tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0,
    // integrations: [
    //   Sentry.replayIntegration()
    // ],
    // replaysSessionSampleRate: 0.1, // 正常采样率
    // replaysOnErrorSampleRate: 1.0, // 错误时全量采样
    beforeSend(event) {
      // 在生产环境中可以过滤敏感信息
      if (process.env.NODE_ENV === 'production') {
        // 移除敏感的用户数据
        if (event.user) {
          delete event.user.email
          delete event.user.ip_address
        }
      }
      return event
    }
    // // 面包屑配置
    // beforeBreadcrumb(breadcrumb) {
    //   // 过滤掉一些不重要的面包屑
    //   if (breadcrumb.category === 'console' && breadcrumb.level === 'debug') {
    //     return null;
    //   }
    //   return breadcrumb;
    // }
  })

  console.info('[Sentry] 初始化成功')
}

/**
 * 设置用户信息到 Sentry
 * @param {Object} userInfo - 用户信息对象
 * @param {Object} options - 可选配置
 * @param {boolean} options.includeEmail - 是否包含邮箱信息（默认：非生产环境包含）
 * @param {boolean} options.includePII - 是否包含个人身份信息（默认：false）
 */
export function setSentryUser(userInfo, options = {}) {
  if (!process.env.VUE_APP_MONITOR_SENTRY_DNS || !userInfo) {
    return
  }

  // 构建安全的用户信息对象
  const sentryUserInfo = {
    // 用户唯一标识
    id: String(userInfo.id || userInfo.userId || userInfo.user_id || 'unknown')

  }

  // 移除空值
  Object.keys(sentryUserInfo).forEach(key => {
    if (sentryUserInfo[key] === undefined || sentryUserInfo[key] === null || sentryUserInfo[key] === '') {
      delete sentryUserInfo[key]
    }
  })

  try {
    Sentry.setUser(sentryUserInfo)
    console.log('[Sentry] User context updated for user:', sentryUserInfo.id)
  } catch (error) {
    console.error('[Sentry] Failed to set user context:', error)
  }
}

/**
 * 清除 Sentry 用户信息
 */
export function clearSentryUser() {
  if (process.env.VUE_APP_MONITOR_SENTRY_DNS) {
    try {
      Sentry.setUser(null)
      console.log('[Sentry] User context cleared')
    } catch (error) {
      console.error('[Sentry] Failed to clear user context:', error)
    }
  }
}

/**
 * 更新用户标签信息
 * @param {Object} tags - 标签对象
 */
export function setSentryUserTags(tags = {}) {
  if (!process.env.VUE_APP_MONITOR_SENTRY_DNS || !tags || typeof tags !== 'object') {
    return
  }

  try {
    Sentry.setTags(tags)
    console.log('[Sentry] User tags updated:', tags)
  } catch (error) {
    console.error('[Sentry] Failed to set user tags:', error)
  }
}

/**
 * 设置用户的额外上下文信息
 * @param {string} key - 上下文键
 * @param {*} value - 上下文值
 */
export function setSentryUserContext(key, value) {
  if (!process.env.VUE_APP_MONITOR_SENTRY_DNS || !key) {
    return
  }

  try {
    Sentry.setContext(key, value)
    console.log(`[Sentry] User context '${key}' updated`)
  } catch (error) {
    console.error(`[Sentry] Failed to set user context '${key}':`, error)
  }
}

/**
 * 手动捕获异常
 * @param {Error} error - 错误对象
 * @param {Object} context - 上下文信息
 */
export function captureException(error, context = {}) {
  if (process.env.VUE_APP_MONITOR_SENTRY_DNS) {
    Sentry.captureException(error, {
      tags: context.tags || {},
      extra: context.extra || {},
      level: context.level || 'error'
    })

    console.log('[Sentry] 异常已上报:', error)
  }
}

/**
 * 添加面包屑
 * @param {Object} breadcrumb - 面包屑信息
 */
export function addBreadcrumb(breadcrumb) {
  if (process.env.VUE_APP_MONITOR_SENTRY_DNS) {
    Sentry.addBreadcrumb(breadcrumb)
  }
}

/**
 * 统一的 API 错误上报函数
 * @param {Error|Object} error - 错误对象或错误信息
 * @param {Object} config - 请求配置对象
 * @param {Object|null} response - 响应对象（可选）
 * @param {string} errorType - 错误类型（如 network_error, business_error 等）
 * @param {string|number|null} errorCode - 错误代码（可选）
 */
export function reportApiError(error, config, response = null, errorType = 'unknown', errorCode = null) {
  console.log('reportApiError', error, config, response, errorType, errorCode)

  // 只上报 HTTP 状态码不是 200 的异常（如 404、502、500 等）
  // 如果有响应且状态码是 200，则不上报
  if (response && response.status === 200) {
    console.log('[Sentry] HTTP 状态码为 200，跳过上报')
    return
  }

  const baseTags = {
    error_type: errorType,
    component: 'apiRequest'
  }

  if (errorCode) {
    baseTags.error_code = errorCode
  }

  const baseExtra = {
    url: config?.url,
    method: config?.method?.toUpperCase(),
    request_data: config?.data,
    response_data: response?.data
  }

  // 网络错误：使用 captureException（保留堆栈）
  if (errorType === 'network_error') {
    captureException(error, {
      tags: {
        ...baseTags,
        status_code: response?.status
      },
      extra: {
        ...baseExtra,
        error_message: error.message,
        is_network_error: !response,
        is_timeout: error.code === 'ECONNABORTED'
      }
    })
  } else {
    // 业务错误也应该使用 captureException
    const errorObj = error instanceof Error ? error : new Error(error.message || error || `API ${errorType} error`)
    captureException(errorObj, {
      tags: baseTags,
      extra: baseExtra
    })
  }
}
